import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import type { IComponent } from "@Components/Component";
import { Component } from "@Components/Component";
import { Alert } from "@Components/Icons/Alert";
import type { IIconBase } from "@Components/Icons/IconBase";
import { requestTrack } from "@Components/Modules/Helpers";

import { Error } from "./icons/Error";
import { Stop } from "./icons/Stop";
import { Success } from "./icons/Success";

import * as css from "./InformationContent.module.css";

export interface IInformationContent extends IComponent {
    informationType: InformationType;
    title?: string;
    description: string;
    button?: IButton;
    onClickButton?: (event: React.MouseEvent<Element>) => void | boolean;
    ajaxTrackingAction?: string;
}

export enum InformationType {
    Success,
    Error,
    Stop,
    Alert,
    None,
}

export class InformationContent extends Component<IInformationContent> {
    private get icon(): React.ReactNode {
        let Component: React.ComponentClass<IIconBase> = null;
        let iconAppearance: "DarkBlue" | "Green" = "DarkBlue";
        switch (this.props.informationType) {
            case InformationType.None:
                return null;
            case InformationType.Error:
                Component = Error;
                break;
            case InformationType.Stop:
                Component = Stop;
                break;
            case InformationType.Alert:
                Component = Alert;
                break;
            default:
                Component = Success;
                iconAppearance = "Green";
                break;
        }

        return <Component iconAppearance={iconAppearance} className={css.alertItem} />;
    }

    public static defaultProps: IInformationContent = {
        informationType: InformationType.Error,
        title: null,
        description: null,
        button: null,
        onClickButton: null,
        ajaxTrackingAction: null,
    };

    constructor(props: IInformationContent) {
        super(props);

        this.onButtonClick = this.onButtonClick.bind(this);
    }

    public componentDidMount() {
        requestTrack(this.props.ajaxTrackingAction);
    }

    public render(): React.ReactNode {
        return (
            <div {...this.rootDefaultAttributes(css.informationContentComponent)}>
                <div className={css.innerContent}>
                    {this.icon}
                    {this.props.title && (
                        <h2 className={css.title} dangerouslySetInnerHTML={{ __html: this.props.title }} />
                    )}
                    <p
                        className={css.description}
                        dangerouslySetInnerHTML={{ __html: this.props.description }}
                    />
                </div>
                {this.props.button && (
                    <div className={css.footer}>
                        <Button
                            {...this.props.button}
                            className={css.button}
                            onClick={this.props.onClickButton}
                        />
                    </div>
                )}
            </div>
        );
    }

    private onButtonClick(event: React.MouseEvent<Element>): void | boolean {
        let result = true;

        if (this.props.onClickButton) {
            result = this.props.onClickButton.call(this, event);
        }

        if (result === false) {
            return false;
        }

        if (this.props.button.onClick) {
            result = this.props.button.onClick.call(this, event);
        }

        return result;
    }
}
